import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { User } from '@entities/User'

import { useCartStore } from '@features/cart/model/useCartStore'
import { useErrorInfoModal } from '@features/modal'


import {
  PingAuthQueryResult,
  useMyBonusTotalQuery,
  usePingAuthLazyQuery,
  usePingAuthQuery,
} from '@shared/generated'

type AuthContextType = {
  user: User | null;
  requireRegistration: boolean;
  isAuthorized: boolean;
  loading: boolean;
  refetchUser: () => Promise<void>;
  fetchPingUser: () => Promise<PingAuthQueryResult>;
};

const AuthContext = createContext<AuthContextType | null>(null)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { t: te } = useTranslation('errors')
  const { openErrorInfoModal } = useErrorInfoModal()
  const [ user, setUser ] = useState<User | null>(null)
  const { data, loading, error, refetch } = usePingAuthQuery()
  const [ fetchPingUser ] = usePingAuthLazyQuery()
  const { loadCarts } = useCartStore()
  const { refetch: refetchBonus } = useMyBonusTotalQuery({
    skip: true, // при авторизцции вызовем вручную
  })

  useEffect(() => {
    if (!error && data) {
      setUser(data.pingAuth ?? null)
    }
  }, [ data, error ])

  const refetchUser = async () => {
    try {
      const result = await refetch()
      setUser(result.data?.pingAuth ?? null)
    }
    catch (e) {
      console.error('Failed to refetch user', e)
      openErrorInfoModal({
        description: te('fetch_user_error'),
      })
      setUser(null)
    }
  }

  const requireRegistration = user?.requireRegistration ?? true

  const isAuthorized = useMemo(() => {
    return Boolean(user && !requireRegistration)
  }, [ user, requireRegistration ])

  useEffect(() => {
    const handleLoadCarts = async () => {
      await loadCarts()
    }

    const syncBonus = async () => {
      try {
        await refetchBonus()
      }
      catch (e) {
        console.error('Failed to refetch bonuses after login', e)
        openErrorInfoModal({
          description: te('fetch_bonus_error'),
        })
      }
    }

    if (isAuthorized) {
      handleLoadCarts()
      syncBonus()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ isAuthorized ])

  const loadingUser = loading || (data && user === null)

  return (
    <AuthContext.Provider
      value={{
        user,
        requireRegistration,
        loading: !!loadingUser,
        isAuthorized,
        refetchUser,
        fetchPingUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
